import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx"; // Make sure you have 'xlsx' installed
import { CloudUpload } from "@mui/icons-material";
import MDButton from "components/MDButton"; // Your custom button
import { useTranslation } from "react-i18next";

// Import your architecture files
import { IMPORT_STRATEGIES } from "components/ImportReview/hooks/importStrategies";
import { detectDataType } from "services/utils";
import PropTypes from "prop-types";
/**
 * @param {Array<string>} allowedTypes - List of types allowed on this page (e.g. ["jobs", "departments"])
 * @param {Object} extraState - Any extra data to pass to the next page (e.g. { companyId })
 * @param {Function} onError - Callback for errors (msg, severity) => void
 * @param {boolean} disabled - specific disable flag
 */
const ImportButton = ({
  allowedTypes = [],
  extraState = {},
  onError,
  disabled = false,
  buttonText,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // 1. Validate File Type
    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];
    if (!validTypes.includes(file.type)) {
      if (onError) onError("Invalid file type. Please upload Excel (.xlsx, .xls)", "error");
      return;
    }

    try {
      const reader = new FileReader();
      reader.readAsBinaryString(file);

      reader.onload = (e) => {
        const binaryString = e.target.result;
        const workbook = XLSX.read(binaryString, { type: "binary" });
        const sheetName = workbook.SheetNames[0]; // Read first sheet

        // Convert to JSON
        const rawData = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { defval: "" });

        if (rawData.length === 0) {
          if (onError) onError("File is empty.", "warning");
          return;
        }

        // 2. Detect Data Type
        const columns = Object.keys(rawData[0]);
        const dataType = detectDataType(columns);

        if (!dataType) {
          if (onError) onError("Could not detect data type. Please check column headers.", "error");
          return;
        }

        // 3. Check if allowed on this page
        // (e.g. You don't want someone importing 'Employees' while on the 'Jobs' page)
        if (allowedTypes.length > 0 && !allowedTypes.includes(dataType)) {
          if (onError)
            onError(
              `Invalid data type. Detected: ${dataType}. Expected: ${allowedTypes.join(", ")}`,
              "error"
            );
          return;
        }

        // 4. Dynamic Data Cleaning
        // We look at the Strategy to find out which columns are actually valid
        const strategy = IMPORT_STRATEGIES[dataType];

        // We call getColumns({}) just to get the field names.
        // We pass empty object because we only care about keys here, not dropdowns.
        const validFields = strategy ? strategy.getColumns({}).map((c) => c.field) : columns;

        const cleanedData = rawData.map((row) => {
          const cleanedRow = {};
          validFields.forEach((field) => {
            // Copy only valid fields, trimming whitespace if string
            let val = row[field];
            if (typeof val === "string") val = val.trim();
            cleanedRow[field] = val ?? "";
          });
          return cleanedRow;
        });

        // 5. Navigate
        // We don't need to pass departments/jobs anymore because the Hook fetches them!
        navigate("/import", {
          state: {
            importedData: cleanedData,
            dataType,
            ...extraState, // e.g. companyId
          },
        });
      };
    } catch (error) {
      console.error(error);
      if (onError) onError("Error reading file.", "error");
    } finally {
      // Reset input so same file can be selected again if needed
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <>
      <input
        type="file"
        accept=".xlsx, .xls"
        ref={fileInputRef}
        onChange={handleFileUpload}
        disabled={disabled}
        style={{ display: "none" }}
        id={`import-file-${allowedTypes.join("-")}`} // Unique ID
      />
      <label htmlFor={`import-file-${allowedTypes.join("-")}`}>
        <MDButton component="span" color="primary" disabled={disabled} startIcon={<CloudUpload />}>
          {/* {buttonText} */}
          {buttonText || t("common.import")}
        </MDButton>
      </label>
    </>
  );
};

ImportButton.propTypes = {
  /** List of data types allowed (e.g. ["jobs", "areas"]) */
  allowedTypes: PropTypes.arrayOf(PropTypes.string),

  /** Extra data to pass to the ImportReview page (e.g. { companyId: 1 }) */
  extraState: PropTypes.object,

  /** Callback function when an error or warning occurs: (message, severity) => void */
  onError: PropTypes.func,

  /** Boolean to disable the button */
  disabled: PropTypes.bool,

  /** Custom text for the button label */
  buttonText: PropTypes.string,
};

export default ImportButton;
